import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Which In flight parents a person opened or closed themselves (spec 150 FR-008).
 *
 * Only choices are stored, not every parent's state: a parent nobody touched
 * keeps following its default (open when a child is stuck, running or waiting
 * on the person), so a release that starts needing attention still opens by
 * itself. Kept on this device, like the file browser's sort.
 */

const STORAGE_KEY = 'overview.inFlight.open';

/** Enough for months of releases; the oldest choices go first. */
export const MAX_CHOICES = 200;

/** `[parentTaskId, open]`, oldest first. */
export type DisclosureChoice = readonly [string, boolean];

export interface InFlightDisclosureState {
  choices: DisclosureChoice[];
  choose: (taskId: string, open: boolean) => void;
}

/** Records a choice as the newest, replacing an older one for the same parent. */
export function recordChoice(
  choices: readonly DisclosureChoice[],
  taskId: string,
  open: boolean,
): DisclosureChoice[] {
  const kept = choices.filter(([id]) => id !== taskId);
  return [...kept, [taskId, open] as const].slice(-MAX_CHOICES);
}

/** What a parent shows: the person's choice, else its default. */
export function isOpen(
  choices: readonly DisclosureChoice[],
  taskId: string,
  defaultOpen: boolean,
): boolean {
  const found = choices.find(([id]) => id === taskId);
  return found ? found[1] : defaultOpen;
}

/**
 * The one parent whose subtasks In motion's tray shows (spec 156): the
 * newest choice among `parentIds`, the cards on screen, if it opened that
 * parent; `null` if it closed it, or there is none. Choices are kept oldest
 * first, so the newest is read first.
 */
export function chosenParent(
  choices: readonly DisclosureChoice[],
  parentIds: readonly string[],
): string | null {
  for (let index = choices.length - 1; index >= 0; index -= 1) {
    const choice = choices[index];
    if (!choice || !parentIds.includes(choice[0])) continue;
    return choice[1] ? choice[0] : null;
  }
  return null;
}

function isChoice(value: unknown): value is DisclosureChoice {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'string' &&
    typeof value[1] === 'boolean'
  );
}

/**
 * Local storage is user-editable text and outlives the shape it was written
 * in, so anything unrecognised is dropped rather than trusted.
 */
export function mergeStoredChoices(
  persisted: unknown,
  current: InFlightDisclosureState,
): InFlightDisclosureState {
  const stored = (persisted as { choices?: unknown } | undefined)?.choices;
  const choices = Array.isArray(stored)
    ? stored.filter(isChoice).slice(-MAX_CHOICES)
    : [];
  // Nothing stored is a first visit; something stored that will not read is
  // worth a line, so the two can be told apart (review of !254).
  const dropped = Array.isArray(stored)
    ? stored.filter((entry) => !isChoice(entry)).length
    : stored === undefined
      ? 0
      : 1;
  if (dropped > 0) {
    console.warn('in flight: stored open/closed choices not recognised', {
      dropped,
    });
  }
  return { ...current, choices };
}

export const useInFlightDisclosure = create<InFlightDisclosureState>()(
  persist(
    (set) => ({
      choices: [],
      choose: (taskId, open) => {
        set((state) => ({
          choices: recordChoice(state.choices, taskId, open),
        }));
      },
    }),
    { name: STORAGE_KEY, merge: mergeStoredChoices },
  ),
);
